import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Rating,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as eventsApi from "@api/events.api";
import * as reviewsApi from "@api/reviews.api";
import type { Event } from "@/types";

const RATING_FIELDS = [
  "ambient",
  "service",
  "speed",
  "starters",
  "mains",
  "deserts",
] as const;

export default function ReviewSubmission() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { notify } = useNotification();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState<Record<string, number>>({
    ambient: 3,
    service: 3,
    speed: 3,
    starters: 3,
    mains: 3,
    deserts: 3,
  });
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    eventsApi.getById(id).then((ev) => {
      setEvent(ev);
      setLoading(false);
    });
  }, [id]);

  const handleSubmit = async () => {
    if (!event?.restaurantId) return;
    setSubmitting(true);
    try {
      await reviewsApi.create({
        restaurantId: event.restaurantId,
        eventId: event.id,
        ambient: ratings.ambient,
        service: ratings.service,
        speed: ratings.speed,
        starters: ratings.starters,
        mains: ratings.mains,
        deserts: ratings.deserts,
        title: title || undefined,
        comment: comment || undefined,
      });
      notify(t("reviews.submit"), "success");
      navigate(`/events/${id}`);
    } catch (err) {
      notify(extractErrorMessage(err, t("common.unexpectedError")), "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  const avgRating =
    Object.values(ratings).reduce((a, b) => a + b, 0) / RATING_FIELDS.length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            {t("reviews.title")}
          </Typography>
          {event?.restaurant && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {event.restaurant.name}
            </Typography>
          )}
        </Box>
      </Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("reviews.overallRating")}: {avgRating.toFixed(1)} / 5
          </Typography>
          <Rating value={avgRating} precision={0.1} readOnly size="large" />
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {RATING_FIELDS.map((field) => (
            <Box
              key={field}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                }}
              >
                {t(`reviews.${field}`)}
              </Typography>
              <Rating
                value={ratings[field]}
                onChange={(_, val) =>
                  setRatings((prev) => ({ ...prev, [field]: val || 1 }))
                }
              />
            </Box>
          ))}
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            label={t("reviews.reviewTitle")}
            placeholder={t("reviews.titlePlaceholder")}
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label={t("reviews.comment")}
            placeholder={t("reviews.commentPlaceholder")}
            fullWidth
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </CardContent>
      </Card>
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleSubmit}
        disabled={submitting}
      >
        {t("reviews.submit")}
      </Button>
    </Box>
  );
}
